import { AfterViewInit, Directive, Injector, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { FormBuilder, FormControl, FormGroup } from '@angular/forms'
import { MatSort } from '@angular/material/sort'
import { MatPaginator } from '@angular/material/paginator'
import { Observable, Subject } from 'rxjs'
import { map, switchMap, takeUntil, tap } from 'rxjs/operators'
import omit from 'lodash-es/omit'

import { AbstractApiDataSource } from '@app/core/services/abstract-api.datasource'
import { TableSettingsComponent } from './settings/table-settings.component'
import { StateService } from '@shared/services/state.service'
import { DateRangeComponent } from '@shared/components/tables/date-range.component'
import { StickerScheduleComponent } from '@shared/common-components/sticker-schedule/sticker-schedule.component'
import { ScheduledReportService } from '@app/modules/client/config/scheduled-reports/scheduled-report.service'
import { ExportFileTypes } from '@shared/models/export-file-types.enum'
import { SettingsService } from '@shared/common-components/settings/settings.service'

interface FormDefaultValue {
  [key: string]: string
}

@Directive()
export abstract class ApiTableComponent implements OnInit, AfterViewInit, OnDestroy {
  form: FormGroup
  columns: string[] = []
  columnsMandatory: string[] = []
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dataSource: AbstractApiDataSource<any>
  fileType: ExportFileTypes

  protected readonly fileName: string
  protected readonly omitStateFields: string[] = []
  protected readonly formBuilder: FormBuilder
  protected readonly scheduledReportService: ScheduledReportService
  protected readonly stateService: StateService
  protected readonly onDestroy$ = new Subject<void>()

  @ViewChild(MatSort, { static: true }) protected readonly sort: MatSort
  @ViewChild(MatPaginator, { static: true }) protected readonly paginator: MatPaginator
  @ViewChild(TableSettingsComponent, { static: true }) protected readonly settings: TableSettingsComponent
  @ViewChild(StickerScheduleComponent, { static: true }) protected readonly stickerScheduleComponent: StickerScheduleComponent
  isBilling: boolean

  constructor(protected injector: Injector) {
    this.formBuilder = injector.get(FormBuilder)
    this.stateService = injector.get(StateService)
    this.scheduledReportService = injector.get(ScheduledReportService)
    this.scheduledReportService = injector.get(ScheduledReportService)
    this.isBilling = injector.get(SettingsService).isBilling
  }

  ngOnInit(): void {
    this.initForm()
    this.initFileExportSubscription()
    this.initScheduledReportSubscription()
    this.initState()
    this.initQueryParamsFormData()
  }

  ngAfterViewInit(): void {
    this.patchForm()
  }

  protected initForm(): void {
    const formDefinition = this.defaultValueForm()
    this.form = this.formBuilder.group(formDefinition)
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected patchForm(): void {}

  private defaultValueForm(): FormDefaultValue {
    return this.columns.reduce((form, key) => ((form[key] = ''), form), {})
  }

  protected initFileExportSubscription(): void {
    this.getFileExportData$().subscribe((content) => this.downloadFile(content))
  }

  protected initScheduledReportSubscription(): void {
    this.stickerScheduleComponent?.scheduledReport.pipe(takeUntil(this.onDestroy$)).subscribe(() => this.dataSource.getScheduledReport())
  }

  protected initState(): void {
    this.initSortState()
    this.initFormState()
  }

  protected initSortState(): void {
    const { active, direction } = this.stateService.getTableSort()

    const adjustedActive = active === 'areaGroupStart' ? undefined : active

    if (this.sort && adjustedActive && direction) {
      this.sort.active = adjustedActive
      this.sort.direction = direction
    }

    this.sort?.sortChange.pipe(takeUntil(this.onDestroy$)).subscribe((sort) => this.stateService.saveTableSort(sort))
  }

  private initQueryParamsFormData(): void {
    this.scheduledReportService.params$.pipe(takeUntil(this.onDestroy$)).subscribe((params) => {
      this.form.patchValue(this.defaultValueForm())
      if (!params) return
      const filterValues = params?.filterValues
      const fields = params?.fields
      const sortValue = params?.sortValue

      if (sortValue) {
        this.sort.active = sortValue.active
        this.sort.direction = sortValue.direction
      }

      if (filterValues && Object.keys(filterValues).length) {
        this.form.patchValue(filterValues)
      }

      if (fields) {
        this.settings.setDisplayedColumns(fields)
      }
    })
  }

  protected initFormState(): void {
    const value = this.stateService.getForm()
    this.form.patchValue(value)

    // this.formStateSubscription()
  }

  private formStateSubscription(): void {
    this.form.valueChanges
      .pipe(
        map((value) => omit(value, this.omitStateFields)),
        takeUntil(this.onDestroy$),
      )
      .subscribe((value) => this.stateService.saveForm(value))
  }

  protected getFileExportData$(): Observable<string> {
    return this.settings.exportFile.pipe(
      tap(({ type }) => (this.fileType = type)),
      switchMap(({ columns, type }) => this.dataSource.getFileData(columns, type)),
      takeUntil(this.onDestroy$),
    )
  }

  protected filterColumns(field: string | string[]): void {
    field = !Array.isArray(field) ? [field] : field
    const withoutFieldToDelete = (column: string) => !field.includes(column)
    this.columns = this.columns.filter(withoutFieldToDelete)
    this.scheduledReportService.setFields(this.columns)
  }

  protected downloadFile(fileContent: string): void {
    const type = this.fileType === ExportFileTypes.pdf ? 'application/pdf' : 'text/csv'
    const blob = new Blob([fileContent], { type })
    const a = document.createElement('a')

    a.href = URL.createObjectURL(blob)
    a.target = '_blank'
    a.download = `${this.fileName}.${this.fileType}`
    document.body.appendChild(a)
    a.click()
    a.remove()
  }

  ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }
}

@Directive()
export abstract class ReportTableComponent extends ApiTableComponent implements AfterViewInit {
  @ViewChild(DateRangeComponent, { static: true }) dateRange: DateRangeComponent

  protected showEachDayCheckbox = false

  ngAfterViewInit(): void {
    super.ngAfterViewInit()
    this.initDateRangeSubscription()
    this.initDataSource()
  }

  initDateRangeSubscription(): void {
    if (!this.dateRange) return

    const initial = this.dateRange.getValue()
    this.form.patchValue(
      {
        startDate: initial.from,
        endDate: initial.to,
        ...(this.showEachDayCheckbox && { sameTimeEachDay: initial.sameTimeEachDay }),
      },
      { emitEvent: false },
    )

    this.dateRange.dateRangeChange.pipe(takeUntil(this.onDestroy$)).subscribe((range) =>
      this.form.patchValue({
        startDate: range.from,
        endDate: range.to,
        ...(this.showEachDayCheckbox && { sameTimeEachDay: range.sameTimeEachDay }),
      }),
    )
  }

  protected abstract initDataSource(): void

  protected initForm(): void {
    super.initForm()

    this.form.addControl('startDate', new FormControl())
    this.form.addControl('endDate', new FormControl())
    if (this.showEachDayCheckbox) {
      this.form.addControl('sameTimeEachDay', new FormControl())
    }
  }
}

@Directive()
export abstract class ReportTableListComponent extends ReportTableComponent implements AfterViewInit {
  searchColumns: string[] = []
  coulmnsInternalTable: string[] = []

  filterColumnsInternalTable(field: string | string[]): void {
    field = !Array.isArray(field) ? [field] : field
    const withoutFieldToDelete = (column: string) => !field.includes(column)

    this.coulmnsInternalTable = this.coulmnsInternalTable.filter(withoutFieldToDelete)
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit()

    const toSearchColumn = (column: string) => `${column}-search`
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    Promise.resolve().then(() => (this.searchColumns = this.columns.map(toSearchColumn)))
  }
}
